// Spacing System
// 4px base grid system for consistent spacing throughout the application

export const spacing = {
  // Base spacing scale (4px grid)
  0: '0',
  1: '0.25rem',  // 4px
  2: '0.5rem',   // 8px
  3: '0.75rem',  // 12px
  4: '1rem',     // 16px
  5: '1.25rem',  // 20px
  6: '1.5rem',   // 24px
  8: '2rem',     // 32px
  10: '2.5rem',  // 40px
  12: '3rem',    // 48px
  16: '4rem',    // 64px
  20: '5rem',    // 80px
  24: '6rem',    // 96px
  32: '8rem',    // 128px
  40: '10rem',   // 160px
  48: '12rem',   // 192px
  56: '14rem',   // 224px
  64: '16rem'    // 256px
} as const;

// Component-specific spacing tokens
export const componentSpacing = {
  // Card spacing
  card: {
    padding: spacing[6],      // 24px
    gap: spacing[4],          // 16px
    margin: spacing[4]        // 16px between cards
  },

  // Button spacing
  button: {
    paddingX: {
      sm: spacing[3],         // 12px
      md: spacing[4],         // 16px
      lg: spacing[6]          // 24px
    },
    paddingY: {
      sm: spacing[2],         // 8px
      md: spacing[3],         // 12px
      lg: spacing[4]          // 16px
    },
    gap: spacing[2]           // 8px between icon and text
  },

  // Form spacing
  form: {
    fieldGap: spacing[4],     // 16px between form fields
    labelGap: spacing[2],     // 8px between label and input
    groupGap: spacing[6],     // 24px between form groups
    sectionGap: spacing[8]    // 32px between form sections
  },

  // Navigation spacing
  navigation: {
    itemPadding: spacing[3],  // 12px
    groupGap: spacing[4],     // 16px
    iconGap: spacing[3],      // 12px
    sectionGap: spacing[6]    // 24px
  },

  // Layout spacing
  layout: {
    containerPadding: spacing[8],  // 32px
    sectionGap: spacing[10],       // 40px
    componentGap: spacing[6],      // 24px
    inlineGap: spacing[4]          // 16px
  },

  // Sidebar spacing
  sidebar: {
    width: '280px',
    padding: spacing[6],      // 24px
    itemHeight: '44px',       // Standard list item height
    groupGap: spacing[4]      // 16px
  },

  // Dashboard spacing
  dashboard: {
    headerPadding: spacing[8], // 32px
    widgetGap: spacing[6],     // 24px
    metricCardPadding: spacing[6], // 24px
    sectionGap: spacing[10]    // 40px
  }
} as const;

// Border radius scale
export const borderRadius = {
  none: '0',
  sm: '0.25rem',   // 4px
  md: '0.375rem',  // 6px
  lg: '0.5rem',    // 8px
  xl: '0.75rem',   // 12px
  '2xl': '1rem',   // 16px
  '3xl': '1.5rem', // 24px
  full: '9999px'   // Fully rounded
} as const;

// Shadow system
export const shadows = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
} as const;

// Z-index scale
export const zIndex = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800
} as const;

// Transition system
export const transitions = {
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '500ms'
  },
  easing: {
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)'
  }
} as const;

// Utility functions for spacing
export const spacingUtils = {
  // Convert spacing token to CSS value
  space: (token: keyof typeof spacing) => spacing[token],
  
  // Create CSS padding shorthand
  padding: (
    top: keyof typeof spacing,
    right?: keyof typeof spacing,
    bottom?: keyof typeof spacing,
    left?: keyof typeof spacing
  ) => {
    const values = [
      spacing[top],
      spacing[right || top],
      spacing[bottom || top],
      spacing[left || right || top]
    ];
    return values.join(' ');
  },

  // Create CSS margin shorthand
  margin: (
    top: keyof typeof spacing,
    right?: keyof typeof spacing,
    bottom?: keyof typeof spacing,
    left?: keyof typeof spacing
  ) => {
    const values = [
      spacing[top],
      spacing[right || top],
      spacing[bottom || top],
      spacing[left || right || top]
    ];
    return values.join(' ');
  }
} as const;